import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import DataTable, { Column } from '../../../components/DataTable/DataTable';
import { fetcher } from '../../../services/fetcher';
import { useTheme } from '../../../contexts/ThemeContext';
import { Badge, Button, Card, SearchIcon, Switch } from '../../../components/ui';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import { ACCOUNTING_TABLE_DEFAULTS } from '../shared/typography';
import { AccountingReportShell } from './shared/AccountingReportShell';
import { PeriodDateRangeFields, formatReportAmountAlways } from './shared/reportUtils';
import { buildReportQuery, useAccountingReportFilters } from './shared/useAccountingReportFilters';

type Row = { lineId: number; levelCode: string; label: string; depth: number; amount: number };

type Response = { rows: Row[]; total: number; dateFrom: string; dateTo: string };

export const ProfitAndLoss: React.FC = () => {
  const { colors } = useTheme();
  const f = useAccountingReportFilters();
  const [useLocalCurrency, setUseLocalCurrency] = useState(true);

  const reportUrl =
    f.run
      ? buildReportQuery('/accounting/reports/profit-and-loss', {
          dateFrom: f.dateFrom,
          dateTo: f.dateTo,
          companyIds: f.companyIdsParam || undefined,
          useLocalCurrency,
        })
      : null;

  const { data, isLoading } = useSWR<Response>(reportUrl, fetcher);
  const rows = data?.rows ?? [];

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        key: 'label',
        header: 'Line',
        render: (v, row) => (
          <span style={{ paddingLeft: `${row.depth * 1.25}rem` }} className={row.depth === 0 ? 'font-semibold' : ''}>
            {v}
          </span>
        ),
      },
      { key: 'levelCode', header: 'Code', width: '9rem' },
      { key: 'amount', header: 'Amount', align: 'right', render: (v: number) => formatReportAmountAlways(v) },
    ],
    [],
  );

  const toolbar = (
    <Card className="p-4 shrink-0 space-y-3">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
        <PeriodDateRangeFields
          periodId={f.periodId}
          periodOptions={f.periodOptions}
          onPeriodChange={(value) => {
            f.setPeriodId(value);
            f.setRun(false);
          }}
          dateFrom={f.dateFrom}
          dateTo={f.dateTo}
          onDateFromChange={(value) => {
            f.setPeriodId('');
            f.setDateFrom(value);
            f.setRun(false);
          }}
          onDateToChange={(value) => {
            f.setPeriodId('');
            f.setDateTo(value);
            f.setRun(false);
          }}
        />
        <Switch label="Local currency" checked={useLocalCurrency} onChange={(e) => { setUseLocalCurrency(e.target.checked); f.setRun(false); }} size="sm" />
      </div>
      <div className={cn('flex items-center gap-2 pt-3 border-t', colors.border)}>
        <Badge variant="secondary" className="h-10 px-3 flex items-center gap-1.5">
          <span className={cn('h-1.5 w-1.5 rounded-full', f.allCompanies ? 'bg-emerald-500' : 'bg-amber-500')} />
          {f.companyScopeLabel}
        </Badge>
        <Button variant="default" onClick={() => f.setRun(true)} disabled={!f.canSearch} className="h-10 px-5 gap-2 ml-auto">
          <SearchIcon className="h-4 w-4" />
          Search
        </Button>
      </div>
    </Card>
  );

  return (
    <AccountingReportShell
      toolbar={toolbar}
      run={f.run}
      loading={isLoading}
      rows={rows}
      columns={columns}
      entityName="Profit and Loss"
      exportFileBase="profit-and-loss"
      exportDateTo={f.dateTo}
      statusBadge={
        f.run ? (
          <Badge variant="info" className="font-normal">
            {formatDateGlobal(f.dateFrom)} – {formatDateGlobal(f.dateTo)}
          </Badge>
        ) : undefined
      }
      statusExtra={
        <div className="flex items-center gap-2">
          <Link
            to="/accounting/statement-lines?type=PL"
            className="text-xs text-indigo-600 hover:underline whitespace-nowrap"
          >
            Configure lines
          </Link>
          <Badge variant="secondary" className="font-normal">
            {useLocalCurrency ? 'Local currency' : 'USD'}
          </Badge>
        </div>
      }
      recordCount={rows.length}
      readyMessage="Choose period and click Search to run P&L."
    >
      <DataTable
        {...ACCOUNTING_TABLE_DEFAULTS}
        data={rows}
        columns={columns}
        keyExtractor={(r) => String(r.lineId)}
        stickyHeader
        emptyMessage="No statement lines."
      />
      {data && (
        <div className="p-4 text-right font-semibold border-t">
          Net result: {formatReportAmountAlways(data.total)}
        </div>
      )}
    </AccountingReportShell>
  );
};
